/**
 * UI Components - Moon Moon Agency  
 * Handles dropdowns, read more functionality, and loader states
 */

/*********************************************************************************************
 * 🚀 LOADER MODULE
 */
const LoaderManager = {
	// Local configuration
	config: {
		classes: {
			loading: 'mm-loading',
			active: 'mm-active',
			expanded: 'mm-expanded'
		},
		selectors: {
			dropdownTrigger: '[data-dropdown-trigger]',
			readMoreToggle: '[data-read-more-toggle]',
			readMoreContainer: '[data-read-more]',
			readMoreText: '[data-read-more-text]',
			readMoreLabelShow: '[data-read-more-show]',
			readMoreLabelHide: '[data-read-more-hide]'
		}
	},

	/**
	 * Add loading state to specified element
	 */
	loadThis(elem) {
		if (!elem) {
			window.log('warn', 'LoaderManager', 'No element provided');
			return;
		}
		elem.classList.add(this.config.classes.loading);
		window.emit('global:loader-activated', { element: elem.tagName });
	}
};

/*********************************************************************************************
 * 📋 DROPDOWN MODULE
 */
const DropdownManager = {
	/**
	 * Close all dropdowns
	 */
	closeAll(dropWrappers) {
		dropWrappers.forEach((el) => {
			el.classList.remove(LoaderManager.config.classes.active);
			el.setAttribute('aria-expanded', 'false');
			if (el.nextElementSibling) {
				el.nextElementSibling.style.maxHeight = null;
			}
		});
	},

	/**
	 * Toggle dropdown
	 */
	toggle(trigger, dropWrappers) {
		// Close other dropdowns
		dropWrappers.forEach((el) => {
			if (el !== trigger) {
				el.classList.remove(LoaderManager.config.classes.active);
				el.setAttribute('aria-expanded', 'false');
				if (el.nextElementSibling) {
					el.nextElementSibling.style.maxHeight = null;
				}
			}
		});

		// Toggle current dropdown
		trigger.classList.toggle(LoaderManager.config.classes.active);
		const content = trigger.nextElementSibling;

		if (content) {
			const isActive = trigger.classList.contains(LoaderManager.config.classes.active);
			trigger.setAttribute('aria-expanded', isActive.toString());
			content.style.maxHeight = isActive ? `${content.scrollHeight}px` : null;

			window.emit('global:dropdown-toggled', {
				active: isActive,
				element: trigger.tagName
			});
		}
	},

	/**
	 * Initialize dropdown behaviors using event delegation
	 */
	init() {
		if (DropdownManager._initialized) return;
		DropdownManager._initialized = true;

		document.addEventListener('click', (e) => {
			const trigger = e.target.closest(LoaderManager.config.selectors.dropdownTrigger);
			if (!trigger) return;

			// Optional: if it's a button, prevent default just in case
			if (trigger.tagName === 'BUTTON') {
				// e.preventDefault(); 
			}

			const all = document.querySelectorAll(LoaderManager.config.selectors.dropdownTrigger);
			this.toggle(trigger, all);
		});

		document.addEventListener('keydown', (e) => {
			const trigger = e.target.closest(LoaderManager.config.selectors.dropdownTrigger);
			if (trigger && (e.key === 'Enter' || e.key === ' ')) {
				e.preventDefault();
				const all = document.querySelectorAll(LoaderManager.config.selectors.dropdownTrigger);
				this.toggle(trigger, all);
			}

			if (e.key === 'Escape' || e.key === 'Esc') {
				const all = document.querySelectorAll(LoaderManager.config.selectors.dropdownTrigger);
				const activeTrigger = Array.from(all).find((el) =>
					el.classList.contains(LoaderManager.config.classes.active)
				);
				const focusInside =
					activeTrigger &&
					(activeTrigger.contains(document.activeElement) ||
						activeTrigger.nextElementSibling?.contains(document.activeElement));
				if (focusInside) {
					this.closeAll(all);
					activeTrigger.focus();
				}
			}
		});

		const defaultOpen = document.querySelector('[data-dropdown-default-open]');
		if (defaultOpen) defaultOpen.style.maxHeight = defaultOpen.scrollHeight + 'px';
	}
};

/*********************************************************************************************
 * 📖 READ MORE MODULE
 */
const ReadMoreManager = {
	/**
	 * Toggle read more state
	 */
	toggle(btn) {
		const container = btn.closest(LoaderManager.config.selectors.readMoreContainer);
		if (!container) return;

		const textElement = container.querySelector(LoaderManager.config.selectors.readMoreText);
		const labelShow = btn.querySelector(LoaderManager.config.selectors.readMoreLabelShow);
		const labelHide = btn.querySelector(LoaderManager.config.selectors.readMoreLabelHide);

		if (!textElement || !labelShow || !labelHide) return;

		const expanded = btn.getAttribute('aria-expanded') === 'true';
		const newState = !expanded;

		// Update state
		textElement.classList.toggle(LoaderManager.config.classes.expanded, newState);
		btn.setAttribute('aria-expanded', newState.toString());
		labelShow.hidden = newState;
		labelHide.hidden = !newState;

		window.emit('global:read-more-toggled', { expanded: newState });
	},

	/**
	 * Initialize read more functionality
	 */
	init() {
		const toggleButtons = window.getElements(LoaderManager.config.selectors.readMoreToggle);
		if (!toggleButtons.length) return;

		toggleButtons.forEach((btn) => {
			// Initialize aria-expanded if not present
			if (!btn.hasAttribute('aria-expanded')) {
				btn.setAttribute('aria-expanded', 'false');
			}

			// Click event
			btn.addEventListener('click', () => {
				this.toggle(btn);
			});

			// Keyboard support (Enter/Space)
			btn.addEventListener('keydown', (e) => {
				if (e.key === 'Enter' || e.key === ' ') {
					e.preventDefault();
					this.toggle(btn);
				}
			});
		});
	}
};

/*********************************************************************************************
 * 🔗 EXTERNAL LINKS MODULE
 */
const ExternalLinksManager = {
	/**
	 * Process external links to open in new tab
	 */
	init() {
		const links = window.getElements('a:not(.consent-wrapper)');
		let processedCount = 0;

		links.forEach((link) => {
			if (link.hostname !== window.location.hostname) {
				link.target = '_blank';
				link.rel = 'noreferrer noopener';
				processedCount++;
			}
		});

	}
};

/*********************************************************************************************
 * 📏 DOCUMENT HEIGHT MODULE
 */
const DocumentHeightManager = {
	/**
	 * Update document height CSS variable
	 */
	update() {
		const newHeight = `${window.innerHeight}px`;
		document.documentElement.style.setProperty('--doc-height', newHeight);
		window.emit('global:document-height-updated', { height: newHeight });
	}
};

/*********************************************************************************************
 * 🎠 SPLIDE MODULE
 */
const SplideManager = {
	/**
	 * Initialize all Splide carousels with their data-splide configurations
	 */
	init() {
		const splideElements = window.getElements('.splide');
		if (!splideElements.length || !window.Splide) return;

		let initializedCount = 0;
		const sectionsWithSplide = [];

		// Contrôles globaux via data-attributes (une seule fois)
		if (!document.documentElement.dataset.splideControlsBound) {
			document.addEventListener('click', (ev) => {
				const btn = ev.target && ev.target.closest && ev.target.closest('[data-splide-control]');
				if (!btn) return;
				ev.preventDefault();

				const action = (btn.dataset && btn.dataset.splideControl) || '';

				// Cherche d'abord une cible explicite
				let targetElem = null;
				const targetSelector = btn.dataset && btn.dataset.splideTarget;
				if (targetSelector) {
					targetElem = document.querySelector(targetSelector);
				}

				const instance = targetElem && targetElem._splide;
				if (!instance) return;

				switch (action) {
					case 'prev':
						instance.go('<');
						break;
					case 'next':
						instance.go('>');
						break;
					default:
						if (action) {
							// Supporte data-splide-control=">+2" ou "=3"
							try { instance.go(action); } catch (e) {}
						}
				}
			}, { passive: false });

			document.documentElement.dataset.splideControlsBound = '1';
		}

		const mountElement = (element) => {
			// Skip already initialized sliders
			if (element._splide) return;

			// Skip product media sliders - they're synced and initialized in mm-product-medias.liquid
			const isProductMediaSlider = element.hasAttribute('data-main-slider') ||
										element.hasAttribute('data-thumbnail-slider');
			if (isProductMediaSlider) return;

			try {
				const configData = element.dataset.splide;
				let splideInstance;

				if (configData) {
					// Nettoyer et vérifier la validité du JSON
					const cleanedData = configData
						.replace(/'/g, '"')  // Remplacer les quotes simples par doubles
						.replace(/(\w+):/g, '"$1":');  // Ajouter des guillemets aux clés sans guillemets

					const config = JSON.parse(cleanedData);
					splideInstance = new Splide(element, config).mount();
					// Expose instance on element for external controls
					element._splide = splideInstance;
					element.classList.remove('mm-splide-pending');
				} else {
					// Configuration par défaut si pas de data-splide
					splideInstance = new Splide(element).mount();
					// Expose instance on element for external controls
					element._splide = splideInstance;
					element.classList.remove('mm-splide-pending');
				}

				// Attendre le chargement des images avant de refresh (fix positionnement initial)
				const images = element.querySelectorAll('img');
				if (images.length > 0) {
					let loadedCount = 0;
					const totalImages = images.length;
					
					const checkAllImagesLoaded = () => {
						loadedCount++;
						if (loadedCount === totalImages && splideInstance) {
							// Toutes les images sont chargées, on refresh le slider
							requestAnimationFrame(() => {
								splideInstance.refresh();
							});
						}
					};

					images.forEach(img => {
						if (img.complete) {
							// Image déjà en cache
							checkAllImagesLoaded();
						} else {
							// Attendre le chargement
							img.addEventListener('load', checkAllImagesLoaded, { once: true });
							img.addEventListener('error', checkAllImagesLoaded, { once: true });
						}
					});
				}

				// Gérer la progress bar si elle existe
				const progressBar = element.querySelector('.splide__progress__bar');
				if (progressBar && splideInstance) {
					const updateProgress = function() {
						try {
							const end = splideInstance.Components.Controller.getEnd() + 1;
							const rate = Math.min((splideInstance.index + 1) / end, 1);
							progressBar.style.width = String(100 * rate) + '%';
						} catch (error) {
							window.log('error', 'SplideManager', 'Progress bar update failed');
						}
					};

					// Initialiser immédiatement et au montage
					splideInstance.on('mounted ready', updateProgress);
					splideInstance.on('move moved', updateProgress);

					// Force l'initialisation après un court délai
					requestAnimationFrame(() => {
						setTimeout(updateProgress, 50);
					});
				}

				// Contrôles externes génériques: disabled auto pour prev/next ciblant ce slider
				if (splideInstance) {
					const candidates = document.querySelectorAll('[data-splide-control][data-splide-target]');
					const controls = Array.from(candidates).filter((btn) => {
						const sel = btn.dataset && btn.dataset.splideTarget;
						if (!sel) return false;
						try {
							const target = document.querySelector(sel);
							return target === element;
						} catch (e) {
							return false;
						}
					});

					if (controls.length) {
						const prevBtns = controls.filter((b) => (b.dataset.splideControl || '').toLowerCase() === 'prev');
						const nextBtns = controls.filter((b) => (b.dataset.splideControl || '').toLowerCase() === 'next');

						const updateExternalArrows = () => {
							try {
								const isLoop = (splideInstance.options && splideInstance.options.type === 'loop') || false;
								const end = splideInstance.Components.Controller.getEnd();
								const atStart = splideInstance.index <= 0;
								const atEnd = splideInstance.index >= end;
								prevBtns.forEach((btn) => { btn.disabled = !isLoop && atStart; });
								nextBtns.forEach((btn) => { btn.disabled = !isLoop && atEnd; });
							} catch (e) { /* noop */ }
						};

						splideInstance.on('mounted move moved updated', updateExternalArrows);
						requestAnimationFrame(updateExternalArrows);
					}
				}

				// Collecter les sections
				const sectionElement = element.closest('section');
				if (sectionElement) {
					const sectionName = sectionElement.className || 'unnamed-section';
					if (!sectionsWithSplide.includes(sectionName)) {
						sectionsWithSplide.push(sectionName);
					}
				}

				initializedCount++;

			} catch (error) {
				window.log('error', 'SplideManager', `JSON parsing failed for element: ${element.className}`);
				window.log('error', 'SplideManager', `Data-splide: ${element.dataset.splide}`);

				// Initialiser avec config par défaut en cas d'erreur
				try {
					const fallback = new Splide(element).mount();
					// Expose fallback instance
					element._splide = fallback;
					initializedCount++;
				} catch (fallbackError) {
					window.log('error', 'SplideManager', `Fallback initialization failed: ${fallbackError.message}`);
				}
			}
		};

		let lastEmittedCount = 0;
		const emitInitialized = () => {
			if (initializedCount === lastEmittedCount) return;
			lastEmittedCount = initializedCount;
			window.emit('global:splide-initialized', {
				count: initializedCount,
				sections: sectionsWithSplide
			});
		};

		const isVisibleNow = (element) => {
			const rect = element.getBoundingClientRect();
			if (rect.width === 0 || rect.height === 0) return false;
			return rect.top < window.innerHeight + 200 && rect.bottom > -200;
		};

		if (!('IntersectionObserver' in window)) {
			splideElements.forEach(mountElement);
			emitInitialized();
			return;
		}

		const observer = new IntersectionObserver((entries) => {
			entries.forEach((entry) => {
				if (!entry.isIntersecting) return;
				observer.unobserve(entry.target);
				mountElement(entry.target);
			});
			emitInitialized();
		}, { rootMargin: '200px 0px' });

		const aMonter = [];
		const aObserver = [];

		splideElements.forEach((element) => {
			if (element.dataset.splideObserved === '1') return;
			element.dataset.splideObserved = '1';

			if (isVisibleNow(element)) {
				aMonter.push(element);
			} else {
				aObserver.push(element);
			}
		});

		aMonter.forEach(mountElement);
		aObserver.forEach((element) => observer.observe(element));

		if (!document.documentElement.dataset.splidePendingBound) {
			document.documentElement.dataset.splidePendingBound = '1';

			const monterCeuxDevenusVisibles = () => {
				const enAttente = window.getElements('.splide');
				const aRattraper = [];

				enAttente.forEach((element) => {
					if (element._splide) return;
					if (element.hasAttribute('data-main-slider') || element.hasAttribute('data-thumbnail-slider')) return;
					if (isVisibleNow(element)) aRattraper.push(element);
				});

				aRattraper.forEach((element) => {
					observer.unobserve(element);
					mountElement(element);
				});

				if (aRattraper.length) emitInitialized();
			};

			document.addEventListener('click', () => {
				requestAnimationFrame(() => setTimeout(monterCeuxDevenusVisibles, 350));
			}, { passive: true });
		}

		emitInitialized();
	}
};

/*********************************************************************************************
 * 🎯 UI COMPONENTS MANAGER
 */
const UIComponentsManager = {
	/**
	 * Initialize all UI components
	 */
	init() {
		DropdownManager.init();
		ReadMoreManager.init();
		ExternalLinksManager.init();
		SplideManager.init();
		DocumentHeightManager.update();
	},

	/**
	 * Handle window resize
	 */
	onResize() {
		DocumentHeightManager.update();
	}
};

/*********************************************************************************************
 * 🌍 GLOBAL ACCESS
 */
window.LoaderManager = LoaderManager;
window.DropdownManager = DropdownManager;  
window.ReadMoreManager = ReadMoreManager;
window.ExternalLinksManager = ExternalLinksManager;
window.SplideManager = SplideManager;
window.DocumentHeightManager = DocumentHeightManager;
window.UIComponentsManager = UIComponentsManager;

/*********************************************************************************************
 * 🔄 BACKWARD COMPATIBILITY
 */
// Active - Used in mm_cart.js
window.mmLoadThis = (elem) => LoaderManager.loadThis(elem);

/*********************************************************************************************
 * 🎯 AUTO-INITIALIZATION
 */
document.addEventListener('DOMContentLoaded', () => {
	UIComponentsManager.init();
});

window.addEventListener('resize', () => {
	UIComponentsManager.onResize();
});

/*********************************************************************************************
 * 🔄 PRODUCT VARIANT CHANGE
 */
document.addEventListener('product:product-reload-complete', () => {
	// Re-initialize other components if needed (ReadMore, etc.)
	ReadMoreManager.init();
	SplideManager.init();
});

/*********************************************************************************************
 * 🔄 PRODUCT RECOMMENDATIONS — Re-initialize Splide after async content injection
 */
document.addEventListener('product-recommendations-loaded', () => {
	SplideManager.init();
});

/*********************************************************************************************
 * 🔄 SHOPIFY EDITOR EVENTS — Re-initialize components on section reload
 */
document.addEventListener('shopify:section:load', (event) => {
	// Destroy old Splide instances inside the reloaded section to avoid duplicates
	const section = event.target;
	if (section) {
		section.querySelectorAll('.splide').forEach((el) => {
			if (el._splide) {
				try { el._splide.destroy(); } catch (e) {}
				delete el._splide;
			}
		});
	}
	// Re-initialize all UI components (Splide, dropdowns, etc.)
	UIComponentsManager.init();
});

document.addEventListener('shopify:section:unload', (event) => {
	const section = event.target;
	if (!section) return;
	// Destroy Splide instances inside the unloaded section
	section.querySelectorAll('.splide').forEach((el) => {
		if (el._splide) {
			try { el._splide.destroy(); } catch (e) {}
			delete el._splide;
		}
	});
});